import { Flex } from '@ifood/pomodoro-components';
import { FormattedMessage, IntlContextConsumer, changeLocale, useIntl } from 'gatsby-plugin-intl';
import React, { FC } from 'react';

import { ROUTER_PATHS } from '@constants/router';
import FooterFacebook from 'images/footer/icon-facebook.svg';
import FooterInstagram from 'images/footer/icon-instagram.svg';
import FooterTwitter from 'images/footer/icon-twitter.svg';
import FooterYouTube from 'images/footer/icon-youtube.svg';
import IFoodFooter from 'images/footer/logo.svg';
import { Languages, LanguagesFields } from 'modules/Core/types';
import { Link } from 'modules/UI';

import { FOOTER_LINKS, LANGUAGES_OPTIONS } from '../../constants';
import { Newsletter } from '../Newsletter';
import {
  Bottom,
  Container,
  Content,
  Copyright,
  CopyrightText,
  FooterLink,
  Icon,
  Section,
  SelectLanguages,
  SocialIcons,
  Title,
  Top,
} from './styles';

const yearFormatting = {
  year: new Date().getFullYear(),
};

const Footer: FC = () => {
  const { formatMessage } = useIntl();

  const languageValues = React.useMemo(
    () =>
      LANGUAGES_OPTIONS.map((value) => ({
        value,
        name: formatMessage({ id: `core.footer.languages.${value}` }),
      })),
    [LANGUAGES_OPTIONS]
  );

  return (
    <Container>
      <Content>
        <Top>
          <Section maxWidth={250}>
            <Title>
              <FormattedMessage id="ifood" />
            </Title>
            <FooterLink dataTestId="contact" href={FOOTER_LINKS.CONTACT}>
              <FormattedMessage id="core.footer.section-link.speak-with-us" />
            </FooterLink>
            <FooterLink mb={0} dataTestId="register" href={FOOTER_LINKS.JOBS}>
              <FormattedMessage id="core.footer.section-link.work-with-us" />
            </FooterLink>
            <SocialIcons>
              <Link dataTestId="facebook" href={FOOTER_LINKS.FACEBOOK}>
                <img alt="facebook" src={FooterFacebook} />
              </Link>
              <Link dataTestId="twitter" href={FOOTER_LINKS.TWITTER}>
                <img alt="twitter" src={FooterTwitter} />
              </Link>
              <Link dataTestId="youtube" href={FOOTER_LINKS.YOUTUBE}>
                <img alt="youtube" src={FooterYouTube} />
              </Link>
              <Link dataTestId="instagram" href={FOOTER_LINKS.INSTAGRAM}>
                <img alt="instagram" src={FooterInstagram} />
              </Link>
            </SocialIcons>
          </Section>
          <Section mt={50}>
            <FooterLink dataTestId="terms-and-conditions" href={FOOTER_LINKS.TERMS}>
              <FormattedMessage id="core.footer.legal.terms-and-conditions" />
            </FooterLink>
            <FooterLink dataTestId="code-of-conduct" href={FOOTER_LINKS.CODE_OF_CONDUCT}>
              <FormattedMessage id="core.footer.legal.code-of-conduct" />
            </FooterLink>
            <FooterLink dataTestId="privacy" href={FOOTER_LINKS.PRIVACY}>
              <FormattedMessage id="core.footer.legal.privacy" />
            </FooterLink>
            <FooterLink dataTestId="medium" href={FOOTER_LINKS.MEDIUM}>
              <FormattedMessage id="core.footer.legal.medium" />
            </FooterLink>
          </Section>
          <Section justifyContent>
            <Flex flexDirection="column" pt="24px">
              <Newsletter />
            </Flex>
            <IntlContextConsumer>
              {({ language: currentLocale }: { language: Languages }): JSX.Element => (
                <SelectLanguages
                  name={LanguagesFields.language}
                  items={languageValues}
                  value={{
                    value: currentLocale,
                    name: formatMessage({ id: `core.footer.languages.${currentLocale}` }),
                  }}
                  onChange={(e): void => {
                    if (e.currentTarget.value !== currentLocale) {
                      changeLocale(e.currentTarget.value);
                    }
                  }}
                />
              )}
            </IntlContextConsumer>
          </Section>
        </Top>
        <Bottom>
          <Copyright>
            <FooterLink noMargin dataTestId="footer-back-to-home" href={ROUTER_PATHS.HOME}>
              <Icon src={IFoodFooter} alt="" />
            </FooterLink>
            <CopyrightText>
              <FormattedMessage id="core.footer.legal.copyright-notice" values={yearFormatting} />
            </CopyrightText>
          </Copyright>
        </Bottom>
      </Content>
    </Container>
  );
};

export default Footer;
